import { Grid } from "antd";
import { useState, useEffect, useMemo } from "react";

export function useIsMobile() {
  const screens = Grid.useBreakpoint();
  const [orientation, setOrientation] = useState(() =>
    typeof globalThis !== "undefined" &&
    globalThis.matchMedia?.("(orientation: landscape)").matches
      ? "landscape"
      : "portrait"
  );
  const [isIOSSafariDesktopMode, setIsIOSSafariDesktopMode] = useState(false);
  const [isDesktopBrowser, setIsDesktopBrowser] = useState(false);

  useEffect(() => {
    // Detect orientation
    const handleOrientationChange = (e: MediaQueryListEvent) => {
      setOrientation(e.matches ? "landscape" : "portrait");
    };

    const mediaQuery = window.matchMedia("(orientation: landscape)");
    mediaQuery.addEventListener("change", handleOrientationChange);

    // Detect desktop browsers
    const detectBrowserType = () => {
      // Check if it's a true desktop browser (not mobile)
      const isMobileDevice =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      // Check for Windows, Mac, or Linux
      const isDesktopOS = /Win|Mac|Linux/i.test(navigator.platform);

      // Additional check for touch capabilities (most desktops don't have touch)
      const hasTouchCapability =
        "ontouchstart" in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.maxTouchPoints > 0;

      // iOS Safari Desktop Mode specific detection
      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      const isSafari =
        /Safari/.test(navigator.userAgent) &&
        !/Chrome/.test(navigator.userAgent);
      const likelyInDesktopMode = window.innerWidth > 980;

      setIsIOSSafariDesktopMode(isIOS && isSafari && likelyInDesktopMode);

      // True desktop is: desktop OS, not a mobile device, and either no touch or has mouse
      setIsDesktopBrowser(
        isDesktopOS &&
          !isMobileDevice &&
          (!hasTouchCapability || "onmousemove" in window)
      );
    };

    detectBrowserType();

    return () => {
      mediaQuery.removeEventListener("change", handleOrientationChange);
    };
  }, []);

  // Determine if mobile based on multiple factors
  const isMobile = useMemo(() => {
    // If it's explicitly identified as a desktop browser, never consider it mobile
    if (isDesktopBrowser && !isIOSSafariDesktopMode) {
      return false;
    }

    // Always consider iOS Safari in desktop mode as mobile
    if (isIOSSafariDesktopMode) {
      return true;
    }

    // Regular breakpoint detection with orientation adjustment for actual mobile devices
    return orientation === "portrait"
      ? !screens.md // In portrait: use standard mobile breakpoint (< 768px)
      : !screens.lg; // In landscape: use larger breakpoint (< 992px)
  }, [
    isDesktopBrowser,
    isIOSSafariDesktopMode,
    orientation,
    screens.md,
    screens.lg,
  ]);

  return {
    isMobile,
    orientation,
    isIOSSafariDesktopMode,
    isDesktopBrowser,
  };
}
